import { Box, Button, Card, CardContent } from '@mui/material'
import { useTranslate } from 'ra-core'
import React, { useEffect, useMemo, useState } from 'react'
import {
  useGetIdentity,
  useGetManyReference,
  useGetOne,
  useNotify,
  useRecordContext,
  useResourceContext,
} from 'react-admin'

import Map, { getMapCircle, MapMarkerIcon } from './Map'
import SensitiveDataAccessDialog from './SensitiveDataAccessDialog'
import { useApi } from '../api/apiProvider'
import { getErrorDataFromError } from '../config/errors'
import { SYSTEM_PERMISSION_READ } from '../config/permissions'
import { useResourcePermissions } from '../domain/permissions'
import hubsConfig from '../resources/hubs/config'
import { getMarkerTypeForDataPoint } from '../resources/vehicleTelematics/list'
import { getRoundedNow } from '../utils/dates'
import GoogleMap from '../vendor/googlemaps'

const VehicleMapField = () => {
  const record = useRecordContext()
  const { identity } = useGetIdentity()
  const [justification, setJustification] = useState('')
  const resource = useResourceContext()
  const notify = useNotify()
  const translate = useTranslate()

  const { data: telemetries } = useGetManyReference(
    'vehicle-telematics',
    {
      target: 'vehicle_id',
      id: record.id,
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'captured_on', order: 'DESC' },
    },
    { enabled: Boolean(identity && !identity.has_restricted_vehicle_position_access) },
  )

  const [hasReadForHubs] = useResourcePermissions(hubsConfig.name, SYSTEM_PERMISSION_READ)
  const { data: hub } = useGetOne(
    hubsConfig.name,
    { id: record.hub_id },
    { enabled: hasReadForHubs && Boolean(record.hub_id) },
  )

  const [fetchVehiclePosition, { data: vehiclePositionData, loading }] = useApi(`/${resource}/position/${record.id}`, {
    method: 'POST',
    body: JSON.stringify({ justification }),
    onFailure: (error) => {
      const { message } = getErrorDataFromError(error)
      notify(translate('mymove.errors.errorWithMessage', { message }), { type: 'error' })
    },
  })

  useEffect(() => {
    if (identity && !identity.has_restricted_vehicle_position_access) {
      fetchVehiclePosition()
    }
  }, [identity?.has_restricted_vehicle_position_access]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = () => {
    if (!justification || loading) return
    fetchVehiclePosition()
  }

  const mapInfo = useMemo(() => {
    let vehicleMarkers = []
    if (telemetries) {
      vehicleMarkers = telemetries.map((item, index, array) => ({
        record: item,
        position: { lat: item.position.latitude, lng: item.position.longitude },
        Icon: MapMarkerIcon,
        markerType: index === array.length - 1 ? 'last' : getMarkerTypeForDataPoint(item),
      }))
    } else if (vehiclePositionData) {
      vehicleMarkers = [
        {
          record: {
            ...vehiclePositionData,
            driving_events: [],
            address: vehiclePositionData.last_known_address,
            captured_on: getRoundedNow().toJSDate().toISOString(),
          },
          position: {
            lat: vehiclePositionData.position.latitude,
            lng: vehiclePositionData.position.longitude,
          },
          Icon: MapMarkerIcon,
          markerType: 'last',
        },
      ]
    }

    let hubCircles = []
    if (hub) {
      hubCircles.push(getMapCircle(hub.geofencing, hub.position))
    }
    return { vehicleMarkers, hubCircles }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(hub), // eslint-disable-line react-hooks/exhaustive-deps
    JSON.stringify(telemetries), // eslint-disable-line react-hooks/exhaustive-deps
    JSON.stringify(vehiclePositionData), // eslint-disable-line react-hooks/exhaustive-deps
    translate,
  ])

  if (!identity) return null

  return identity.has_restricted_vehicle_position_access && !vehiclePositionData ? (
    <BlurredMapRequestAccess
      justification={justification}
      loading={loading}
      onConfirm={handleConfirm}
      setJustification={setJustification}
    />
  ) : (
    <Map markers={mapInfo.vehicleMarkers} circles={mapInfo.hubCircles} />
  )
}

export default VehicleMapField

const BlurredMapRequestAccess = ({ justification, loading, onConfirm, setJustification }) => {
  const resource = useResourceContext()
  const [isJustificationDataDialogOpen, setIsJustificationDataDialogOpen] = useState(false)
  const translate = useTranslate()

  const handleClose = () => {
    setIsJustificationDataDialogOpen(false)
    setJustification('')
  }

  const handleConfirm = () => {
    onConfirm()
    handleClose()
  }

  return (
    <Card>
      <CardContent sx={{ position: 'relative', padding: '0px !important' }}>
        <Box sx={{ filter: 'blur(4px)', height: 360, m: '-0.75px' }}>
          <GoogleMap />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsJustificationDataDialogOpen(true)}
          sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          {translate(`resources.${resource}.mapAccessRequest.buttonTitle`)}
        </Button>
        <SensitiveDataAccessDialog
          handleClose={handleClose}
          handleConfirm={handleConfirm}
          isConfirmDisabled={!justification}
          isOpen={isJustificationDataDialogOpen}
          loading={loading}
          onJustificationChange={(event) => setJustification(event.target.value)}
          translationPrefix={`resources.${resource}.show.locationData`}
        />
      </CardContent>
    </Card>
  )
}
